import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  AppBar,
  Box,
  Button,
  Toolbar,
  Typography,
  CssBaseline,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import Home from './pages/Home';
import About from './pages/About';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import DriverTraining from './pages/DriverTraining';
import DriverSchedule from './pages/DriverSchedule';
import DriverQuiz from './pages/DriverQuiz';
import { AuthProvider, driverNeedsKnowledgeTraining, useAuth } from './context/AuthContext';
import './styles.css';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    background: {
      default: '#ffffff',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 6px 25px rgba(0,0,0,0.2)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '1.1rem',
          padding: '8px 16px',
        },
      },
    },
  },
});

function NavigationMenu() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { currentUser, logout, quizInProgress } = useAuth();
  const needsTraining = driverNeedsKnowledgeTraining(currentUser);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClick = (path: string) => {
    setAnchorEl(null);
    navigate(path);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = async () => {
    setAnchorEl(null);
    await logout();
    navigate('/login');
  };

  const menuItems = [
    {
      label: 'Home',
      path: '/',
      icon: <HomeIcon sx={{ fontSize: '1.1rem !important' }} />,
      mobileIcon: <HomeIcon sx={{ mr: 1, fontSize: 20 }} />,
    },
    {
      label: 'About',
      path: '/about',
      icon: <InfoIcon sx={{ fontSize: '1.1rem !important' }} />,
      mobileIcon: <InfoIcon sx={{ mr: 1, fontSize: 20 }} />,
    },
  ];

  if (isMobile) {
    return (
      <>
        <IconButton
          size="large"
          edge="end"
          color="inherit"
          aria-label="menu"
          onClick={handleMenu}
          sx={{
            ml: 0.5,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          sx={{
            '& .MuiPaper-root': {
              bgcolor: 'primary.main',
              color: 'white',
              minWidth: '220px',
              mt: 5,
            },
          }}
        >
          {menuItems.map((item) => (
            <MenuItem
              key={item.path}
              onClick={() => handleMenuClick(item.path)}
              sx={{
                py: 1.5,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: 'grey.300',
                },
              }}
            >
              {item.mobileIcon}
              <Typography variant="body1">{item.label}</Typography>
            </MenuItem>
          ))}
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 0.5 }} />
          {currentUser && !needsTraining && !quizInProgress && (
            <MenuItem
              onClick={() => handleMenuClick('/schedule')}
              sx={{
                py: 1.5,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: 'grey.300',
                },
              }}
            >
              <CalendarMonthIcon sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="body1">My Schedule</Typography>
            </MenuItem>
          )}
          {currentUser && !quizInProgress && (
            <MenuItem
              onClick={() => handleMenuClick('/training')}
              sx={{
                py: 1.5,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: 'grey.300',
                },
              }}
            >
              <MenuBookIcon sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="body1">Knowledge Base</Typography>
            </MenuItem>
          )}
          {currentUser && (currentUser.role === 'admin' || currentUser.role === 'manager') && (
            <MenuItem
              onClick={() => handleMenuClick('/admin')}
              sx={{
                py: 1.5,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: 'grey.300',
                },
              }}
            >
              <AdminPanelSettingsIcon sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="body1">
                {currentUser.role === 'manager' ? 'Manager Portal' : 'Admin Portal'}
              </Typography>
            </MenuItem>
          )}
          {currentUser ? (
            <MenuItem
              onClick={handleLogoutClick}
              sx={{
                py: 1.5,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: 'grey.300',
                },
              }}
            >
              <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="body1">Logout ({currentUser.name.split(' ')[0]})</Typography>
            </MenuItem>
          ) : (
            <MenuItem
              onClick={() => handleMenuClick('/login')}
              sx={{
                py: 1.5,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: 'grey.300',
                },
              }}
            >
              <LoginIcon sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="body1">Login</Typography>
            </MenuItem>
          )}
        </Menu>
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
      {menuItems.map((item) => (
        <Button
          key={item.path}
          color="inherit"
          component={Link}
          to={item.path}
          size="small"
          startIcon={item.icon}
          sx={{
            whiteSpace: 'nowrap',
            fontSize: '0.95rem',
            px: 1.5,
            py: 0.75,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              transform: 'translateY(-2px)',
              transition: 'transform 0.2s ease-in-out',
              color: 'grey.300',
            },
          }}
        >
          {item.label}
        </Button>
      ))}

      {currentUser && !needsTraining && !quizInProgress && (
        <Button
          color="inherit"
          component={Link}
          to="/schedule"
          size="small"
          startIcon={<CalendarMonthIcon sx={{ fontSize: '1.1rem !important' }} />}
          sx={{
            whiteSpace: 'nowrap',
            fontSize: '0.95rem',
            px: 1.5,
            py: 0.75,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              transform: 'translateY(-2px)',
              transition: 'transform 0.2s ease-in-out',
              color: 'grey.300',
            },
          }}
        >
          My Schedule
        </Button>
      )}

      {currentUser && !quizInProgress && (
        <Button
          color="inherit"
          component={Link}
          to="/training"
          size="small"
          startIcon={<MenuBookIcon sx={{ fontSize: '1.1rem !important' }} />}
          sx={{
            whiteSpace: 'nowrap',
            fontSize: '0.95rem',
            px: 1.5,
            py: 0.75,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              transform: 'translateY(-2px)',
              transition: 'transform 0.2s ease-in-out',
              color: 'grey.300',
            },
          }}
        >
          Knowledge Base
        </Button>
      )}

      {currentUser && (currentUser.role === 'admin' || currentUser.role === 'manager') && (
        <Button
          color="inherit"
          component={Link}
          to="/admin"
          size="small"
          startIcon={<AdminPanelSettingsIcon sx={{ fontSize: '1.1rem !important' }} />}
          sx={{
            whiteSpace: 'nowrap',
            fontSize: '0.95rem',
            px: 1.5,
            py: 0.75,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              transform: 'translateY(-2px)',
              transition: 'transform 0.2s ease-in-out',
              color: 'grey.300',
            },
          }}
        >
          {currentUser.role === 'manager' ? 'Manager' : 'Admin'}
        </Button>
      )}

      {currentUser ? (
        <Button
          color="inherit"
          onClick={handleLogoutClick}
          size="small"
          startIcon={<LogoutIcon sx={{ fontSize: '1.1rem !important' }} />}
          sx={{
            whiteSpace: 'nowrap',
            fontSize: '0.95rem',
            px: 1.5,
            py: 0.75,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              transform: 'translateY(-2px)',
              transition: 'transform 0.2s ease-in-out',
              color: 'grey.300',
            },
          }}
        >
          Logout
        </Button>
      ) : (
        <Button
          color="inherit"
          component={Link}
          to="/login"
          size="small"
          startIcon={<LoginIcon sx={{ fontSize: '1.1rem !important' }} />}
          sx={{
            whiteSpace: 'nowrap',
            fontSize: '0.95rem',
            px: 1.5,
            py: 0.75,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              transform: 'translateY(-2px)',
              transition: 'transform 0.2s ease-in-out',
              color: 'grey.300',
            },
          }}
        >
          Login
        </Button>
      )}
    </Box>
  );
}

function UserWelcomeLabel() {
  const { currentUser } = useAuth();
  if (!currentUser) return null;

  return (
    <Typography
      variant="body2"
      sx={{
        display: { xs: 'none', sm: 'block' },
        position: { sm: 'absolute' },
        bottom: { sm: 10, md: 12 },
        right: { sm: 24 },
        width: { sm: 'auto' },
        mt: { sm: 0 },
        pt: { sm: 0 },
        pb: { sm: 0 },
        zIndex: 5,
        color: 'rgba(255, 255, 255, 0.92)',
        fontFamily: "'Plus Jakarta Sans', 'Roboto', sans-serif",
        fontSize: { xs: '0.85rem', sm: '0.88rem' },
        letterSpacing: '0.01em',
        textAlign: { xs: 'center', sm: 'right' },
      }}
    >
      Welcome, <strong>{currentUser.name}</strong>
    </Typography>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  const { currentUser, quizInProgress } = useAuth();
  const needsTraining = driverNeedsKnowledgeTraining(currentUser);
  const path = location.pathname;
  const trainingPaths = ['/training', '/knowledge-base', '/quiz', '/login'];

  let redirectTo = '';
  if (currentUser && quizInProgress && path !== '/quiz') {
    redirectTo = '/quiz';
  } else if (currentUser && needsTraining && !trainingPaths.includes(path)) {
    redirectTo = '/training';
  }

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <TransitionGroup>
      <CSSTransition key={location.key} timeout={300} classNames="slide">
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/schedule" element={<DriverSchedule />} />
          <Route path="/training" element={<DriverTraining />} />
          <Route path="/knowledge-base" element={<DriverTraining />} />
          <Route path="/quiz" element={<DriverQuiz />} />
        </Routes>
      </CSSTransition>
    </TransitionGroup>
  );
}

function App() {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Box
            sx={{
              minHeight: '100vh',
              bgcolor: 'background.default',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <AppBar
              position="fixed"
              elevation={0}
              sx={{
                background: 'linear-gradient(90deg, #0D47A1 0%, #1565C0 50%, #1976d2 100%)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(8px)',
                boxShadow:
                  '0 4px 8px -1px rgba(0, 0, 0, 0.2), 0 8px 16px -2px rgba(0, 0, 0, 0.25), 0 12px 24px -4px rgba(0, 0, 0, 0.3)',
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1100,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -1,
                  left: 0,
                  width: '100%',
                  height: '5px',
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), transparent)',
                  pointerEvents: 'none',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  height: '3px',
                  width: '100%',
                  background:
                    'linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.1) 100%)',
                  animation: 'shimmer 3s infinite linear',
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '-200% 0' },
                    '100%': { backgroundPosition: '200% 0' },
                  },
                  backgroundSize: '200% 100%',
                  zIndex: 1,
                },
              }}
            >
              <Toolbar
                sx={{
                  minHeight: { xs: 56, sm: '200px', md: '200px' },
                  py: { xs: 0.75, sm: 2.5 },
                  px: { xs: 1.5, sm: 3 },
                  flexDirection: { xs: 'row', sm: isMedium ? 'column' : 'row' },
                  alignItems: 'center',
                  justifyContent: { xs: 'space-between', sm: isMedium ? 'center' : 'space-between' },
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  mx: { xs: 0, sm: 2, md: 4 },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                {/* Logo and Title Area */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: { xs: 1, sm: '0 1 auto' },
                    minWidth: 0,
                    flexDirection: 'row',
                    textAlign: { xs: 'left', sm: 'left' },
                  }}
                >
                  <IconButton
                    component={Link}
                    to="/"
                    disableRipple={true}
                    sx={{
                      mr: { xs: 1, sm: 4 },
                      mb: 0,
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                      padding: 0,
                    }}
                  >
                    <Box
                      component="img"
                      src="/sds-logo.png"
                      alt="SDS Logo"
                      sx={{
                        height: { xs: 44, sm: 160 },
                        width: 'auto',
                        display: 'block',
                        filter:
                          'drop-shadow(0px 2px 3px rgba(0,0,0,0.4)) drop-shadow(0px 4px 8px rgba(0,0,0,0.3))',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          filter:
                            'drop-shadow(0px 3px 4px rgba(0,0,0,0.5)) drop-shadow(0px 6px 10px rgba(0,0,0,0.4))',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    />
                  </IconButton>
                  <Box
                    component={Link}
                    to="/"
                    sx={{
                      textDecoration: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: { xs: 'flex-start', sm: 'flex-start' },
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: "'Plus Jakarta Sans', 'Roboto', sans-serif",
                        fontWeight: 800,
                        fontSize: { xs: '0.95rem', sm: '1.65rem', md: '1.95rem', lg: '2.25rem' },
                        whiteSpace: 'nowrap',
                        overflow: { xs: 'hidden', sm: 'visible' },
                        textOverflow: { xs: 'ellipsis', sm: 'clip' },
                        maxWidth: '100%',
                        letterSpacing: '-0.02em',
                        lineHeight: 1.15,
                        textAlign: { xs: 'left', sm: 'left' },
                        background: 'linear-gradient(180deg, #FFFFFF 0%, #E3F2FD 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        filter: 'drop-shadow(0 2px 10px rgba(0, 0, 0, 0.22))',
                      }}
                    >
                      Success Delivery Services
                    </Typography>

                    <Box
                      sx={{
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center',
                        gap: 1,
                        mt: 0.6,
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 20, sm: 28 },
                          height: '2px',
                          borderRadius: '2px',
                          background: 'linear-gradient(90deg, #64B5F6 0%, rgba(100,181,246,0.2) 100%)',
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "'Plus Jakarta Sans', 'Roboto', sans-serif",
                          fontWeight: 600,
                          fontSize: { xs: '0.65rem', sm: '0.72rem', md: '0.76rem' },
                          letterSpacing: '0.18em',
                          textTransform: 'uppercase',
                          color: 'rgba(227, 242, 253, 0.88)',
                        }}
                      >
                        Medical &amp; Pharmaceutical Logistics
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {isMobile && (
                  <Box sx={{ flexShrink: 0 }}>
                    <NavigationMenu />
                  </Box>
                )}

                {/* Desktop Navigation */}
                {!isMobile && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      borderRadius: '30px',
                      padding: '4px 6px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(5px)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                      ml: { sm: 0, lg: 2 },
                      mt: isMedium ? 1.5 : 0,
                      flexShrink: 0,
                      '& .MuiButton-root': {
                        mx: 0.25,
                        borderRadius: '20px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                          boxShadow: 'none',
                        },
                      },
                    }}
                  >
                    <NavigationMenu />
                  </Box>
                )}
                <UserWelcomeLabel />
              </Toolbar>
            </AppBar>

            <Box component="main">
              <AnimatedRoutes />
            </Box>
          </Box>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
